import motion from "../../motion-tokens.json" with { type: "json" };
import { motionAllowed } from "./motion.js";
import { holdListMotion } from "../view-transition.js";

/**
 * Copy prompt's way out (P83, D189 of documentation/pr-plans/insights-simplify-plan.md). Once a copy in the
 * Draft an article drawer made its search a target keyword, the drawer hands its close to closeAfterCopy():
 * the drawer stays until LINGER after the press, so its footer line can be read, and until the row it was
 * opened from has folded into its slim Done row and that fold's motion has ended. Then it closes, and
 * COPY_CLOSED_EVENT tells the page: the Insights toast, which waited so it never covered the footer line,
 * shows, and a pill with the search rises from the folded row, arcs into the top bar's Target keywords
 * button while the drawer slides away (the pill is drawn above it) and shrinks into it; the button pulses
 * once. A copy made from no row (Stats, Explore) pulses the button alone. The flight and the landed state are
 * long enough to be watched (P101, D217): on Nathan's call of 23 Oct nobody saw the search go anywhere.
 *
 * The button is an icon pill (P82, D198): it opens to its name as the search leaves the row, so the search lands
 * in "Target keywords" (D217), and stays open OPEN_MS past the landing; once its landing pulse is over it is marked
 * new (NEW_CLASS), whose ring pulses three times and leaves a still dot until the person opens Target keywords. The
 * landed tint stays until that last pulse ends. The page draws that mark from the server on every load (SiteController),
 * as a still dot; this adds it, with its pulses (MARK_PULSE_CLASS), only as the copy lands.
 *
 * The flight is browser-only: the target is already made, and the Undo that reverses it is the toast's and
 * the slim row's. Undo (the toast's UNDO event, or the server's untargeted event) stops a flight or pulse
 * under way, and takes back a mark the copy left while the name is still open. Under reduced motion nothing
 * here moves: the footer line and the toast say it, and the pill opens to its name with its dot, still.
 *
 * Imports nothing but the motion helpers and the list motion's hold, so it runs under Node as it does in the
 * browser (InsightsTargetFlightTest).
 */
export const COPY_CLOSED_EVENT = "draft-article-copy-closed";

/** The events that undo a copy's target: the toast's Undo (DraftArticle::UNDO_EVENT) and the server's answer. */
export const CANCEL_EVENTS = ["draft-article-untarget", "site-target-keyword-removed"];

export const LINK_SELECTOR = "[data-target-keywords-link]";

/** Set on the document element while a list's view transition runs (view-transition.js moveRows()). */
export const LIST_MOTION_ATTRIBUTE = "data-list-motion";

/** How long past the drawer's own pause it waits for the row to fold before closing anyway: short, so a slow
 *  answer never holds the drawer; the flight leaves from the row as it stands, and the focus waits for the fold. */
export const FOLD_WAIT_MS = 600;

/** How often the close checks its conditions: a timer, not frames, so a tab in the background still closes. */
export const CHECK_MS = 40;

/** The pill's whole flight: its rise, its travel (most of it, FLIGHT_RISE to FLIGHT_LAND) and its shrink into the button. */
export const FLIGHT_MS = 1200;

/** Where in the flight the pill has risen, and where it reaches the button, as fractions of FLIGHT_MS. */
export const FLIGHT_RISE = 0.12;
export const FLIGHT_LAND = 0.84;

/** How many straight steps draw the pill's arc: enough that it reads as one curve. */
export const ARC_STEPS = 12;

/** The highest the arc takes the pill: just inside the top of the window, so it never flies off the screen. */
const ARC_TOP_MARGIN = 8;

/** The pill in flight: the app's primary pill, fixed over everything, hidden from assistive tech (the toast said it). */
export const PILL_CLASSES =
    "pointer-events-none fixed left-0 top-0 z-[1001] inline-flex items-center whitespace-nowrap rounded-full bg-brand-secondary-500 px-3 py-1 text-[13px] font-semibold text-white shadow-float will-change-transform";

/** The button taking the pill: one pulse of its ring (tailwind.config.js, target-pulse), under motion-safe. */
export const PULSE_CLASS = "motion-safe:animate-target-pulse";

/** The landed tint, held until the button's last pulse, the mark's third, has ended (Reine, 26 Oct). */
export const TINT_CLASSES = ["!border-brand-secondary-500", "!bg-brand-secondary-50", "!text-brand-secondary-onlight"];

/** When the mark's pulses take the ring from the landing pulse: that pulse (three times the view token) and a beat. */
export const LANDED_MS = 2000;

/** The mark's three pulses as it arrives (tailwind.config.js, target-pulse-thrice: twice the view token, three times). */
export const MARK_PULSES_MS = motion.duration.view * 2 * 3;

/** When the button, opened as the pill leaves, is measured again to aim the landing: once its label's open (the
 *  `show` token, x-insights.icon-pill) is over and laid out. */
export const REAIM_MS = motion.duration.show + 50;

/** The icon pill holding its name open (x-insights.icon-pill). */
export const OPEN_CLASS = "is-open";

/** The icon pill marked new: three pulses of its ring as it comes (MARK_PULSES_MS), then a still dot. From LANDED_MS,
 *  the button has moved for 4.4 s since the search landed, under WCAG 2.2.2's five (InsightsTargetFlightTest holds it). */
export const NEW_CLASS = "is-new";

/** The mark's three pulses (tailwind.config.js, target-pulse-thrice), added only here as a copy marks the pill, under
 *  motion-safe: the page draws NEW_CLASS on every load while a target is unseen, and a reload must show the dot still. */
export const MARK_PULSE_CLASS = "motion-safe:animate-target-pulse-thrice";

/** How long the pill keeps its name open once a search lands in it: past the mark's last pulse, so it can be read. */
export const OPEN_MS = 5000;

/** The pill's travel: it leaves and arrives gently and is quickest in the middle, at no more than about one and a
 *  half times its average speed, so the eye can follow it all the way. */
function travelEase(t) {
    return (1 - Math.cos(Math.PI * t)) / 2;
}

const round = (value, places) => Math.round(value * 10 ** places) / 10 ** places;

/**
 * The pill's keyframes, in viewport coordinates, from the folded row's name (`from`) to the centre of the
 * Target keywords button (`to`), the pill's own box being `pill`: it rises a little as it appears, arcs
 * above both (never above the top of the window), lands centred on the button at three quarters of its size
 * and shrinks into it.
 *
 * Every keyframe has its time (`offset`) and the flight itself runs linear, so the travel's ease is in where
 * the arc's steps sit. Eased as one animation, the travel was over in the first fifth of the flight and the
 * pill sat shrinking on the button for the rest (P101): that is why nobody saw it fly.
 *
 * @param {{left: number, top: number, width: number, height: number}} from
 * @param {{left: number, top: number, width: number, height: number}} to
 * @param {{width: number, height: number}} pill
 */
export function flightKeyframes(from, to, pill) {
    const x0 = from.left - 12;
    const y0 = from.top + (from.height - pill.height) / 2;
    const x1 = to.left + to.width / 2 - pill.width / 2;
    const y1 = to.top + to.height / 2 - pill.height / 2;
    const risen = y0 - 10;
    const higher = Math.min(risen, y1);
    // The arc's top, 48px above the higher end and inside the window, and the control point of the curve
    // whose highest point that is.
    const top = Math.min(Math.max(higher - 48, ARC_TOP_MARGIN), higher);
    const control = top - Math.sqrt((risen - top) * (y1 - top));
    const arc = [];

    for (let step = 1; step < ARC_STEPS; step++) {
        const t = step / ARC_STEPS;
        const u = travelEase(t);
        const x = x0 + (x1 - x0) * u;
        const y = (1 - u) ** 2 * risen + 2 * u * (1 - u) * control + u ** 2 * y1;

        arc.push({
            transform: `translate(${round(x, 1)}px, ${round(y, 1)}px) scale(${round(1.02 - 0.3 * u, 3)})`,
            opacity: 1,
            offset: FLIGHT_RISE + (FLIGHT_LAND - FLIGHT_RISE) * t,
        });
    }

    return [
        { transform: `translate(${x0}px, ${y0}px) scale(1)`, opacity: 0, offset: 0, easing: motion.ease },
        { transform: `translate(${x0}px, ${risen}px) scale(1.02)`, opacity: 1, offset: FLIGHT_RISE },
        ...arc,
        { transform: `translate(${x1}px, ${y1}px) scale(0.72)`, opacity: 1, offset: FLIGHT_LAND, easing: motion.ease },
        { transform: `translate(${x1}px, ${y1}px) scale(0.3)`, opacity: 0, offset: 1 },
    ];
}

// CSS.escape where there is one (not under Node).
const cssEscape = (value) => (typeof CSS !== "undefined" && typeof CSS.escape === "function" ? CSS.escape(value) : String(value).replace(/["\\]/g, "\\$&"));

/** The folded row's name, in the activity and row the drawer was opened from, or null while it has not folded. */
export function foldedName(document, { activity, rowId }) {
    if (!activity || !rowId || typeof document.querySelector !== "function") {
        return null;
    }


    return document.querySelector(`[data-insights-activity="${cssEscape(activity)}"] tr[data-row][data-row-id="${cssEscape(rowId)}"] [data-notice] strong`);
}

/** The row's own name, as it stands before it folds, in the activity and row the drawer was opened from, or null. */
export function rowName(document, { activity, rowId }) {
    if (!activity || !rowId || typeof document.querySelector !== "function") {
        return null;
    }

    const name = document.querySelector(`[data-insights-activity="${cssEscape(activity)}"] tr[data-row][data-row-id="${cssEscape(rowId)}"] [data-row-name]`);

    // Hidden (the row folded, or is on a page not shown), it has no box to leave from.
    return name !== null && name.getBoundingClientRect().width > 0 ? name : null;
}

function isListMoving(document) {
    return document.documentElement?.hasAttribute?.(LIST_MOTION_ATTRIBUTE) === true;
}

/**
 * Closes the drawer after a copy that made the target (`detail`: {activity, rowId, query}), then sends
 * COPY_CLOSED_EVENT with `detail`. It closes once `lingerMs` has passed since the press, the row the copy came
 * from has folded and no list is moving (a drawer closed inside a view transition would sit frozen in its old
 * picture, then jump), or FOLD_WAIT_MS later at the latest. A copy from no row waits for nothing but the pause.
 *
 * @param {{activity?: string|null, rowId?: string|null, query?: string}} detail
 * @param {{pressedAt: number, lingerMs: number, close: () => void}} options
 */
export function closeAfterCopy(detail, { pressedAt, lingerMs, close }, env = {}) {
    const document = env.document ?? globalThis.document;
    const target = env.window ?? globalThis.window;
    const now = env.now ?? (() => performance.now());
    const later = env.setTimeout ?? ((callback, ms) => setTimeout(callback, ms));
    const expectsRow = Boolean(detail?.activity && detail?.rowId);

    const check = () => {
        const waited = now() - pressedAt;
        const isSettled = (!expectsRow || foldedName(document, detail) !== null) && !isListMoving(document);

        if (waited < lingerMs || (!isSettled && waited < lingerMs + FOLD_WAIT_MS)) {
            later(check, CHECK_MS);

            return;
        }

        close();
        target?.dispatchEvent?.(new CustomEvent(COPY_CLOSED_EVENT, { detail }));
    };

    check();
}

/**
 * One flight at a time, held outside the DOM: a new copy, or Undo, stops the one under way.
 */
let current = null;

export function cancelFlight() {
    if (current === null) {
        return;
    }

    const { pill, animation, link, timers, opened, release } = current;

    current = null;
    timers.forEach((timer) => clearTimeout(timer));
    release?.();
    animation?.cancel?.();
    pill?.remove?.();
    link?.classList?.remove?.(PULSE_CLASS, ...TINT_CLASSES);

    // The name this flight opened before it landed, when no mark (markNew) holds it open: Undo shuts it again.
    if (opened && marking?.link !== link) {
        link?.classList?.remove?.(OPEN_CLASS);
    }
}

/**
 * The mark a copy left on the pill while its name is still open: an Undo in that time takes it back off, unless the
 * pill was already marked new before the copy (a target made earlier that this person has not seen yet).
 */
let marking = null;

function endMarking() {
    marking?.timers.forEach((timer) => clearTimeout(timer));
    marking = null;
}

/**
 * The pill opens to its name for OPEN_MS and is marked new, at once or once `newAfterMs` has passed (its landing
 * pulse). Marked again, the mark pulses again.
 */
export function markNew(link, newAfterMs = 0) {
    const wasNew = marking?.link === link ? marking.wasNew : link.classList.contains(NEW_CLASS);

    endMarking();

    const record = { link, wasNew, timers: [] };
    const mark = () => {
        link.classList.remove(NEW_CLASS, MARK_PULSE_CLASS);
        // A style read between removing and adding, so a mark made again starts its three pulses again.
        void link.offsetWidth;
        link.classList.add(NEW_CLASS, MARK_PULSE_CLASS);
        record.timers.push(setTimeout(() => link.classList.remove(MARK_PULSE_CLASS), MARK_PULSES_MS));
    };

    marking = record;
    link.classList.add(OPEN_CLASS);

    if (newAfterMs > 0) {
        record.timers.push(setTimeout(mark, newAfterMs));
    } else {
        mark();
    }

    record.timers.push(setTimeout(() => {
        link.classList.remove(OPEN_CLASS);

        if (marking === record) {
            marking = null;
        }
    }, OPEN_MS));
}

/** An Undo while the pill's name is still open: the copy's mark comes off, and the pill is as it was before it. */
export function unmarkNew() {
    if (marking === null) {
        return;
    }

    const { link, wasNew } = marking;

    endMarking();
    link.classList.remove(OPEN_CLASS, MARK_PULSE_CLASS);

    if (!wasNew) {
        link.classList.remove(NEW_CLASS);
    }
}

function pulse(link, flight) {
    link.classList.remove(PULSE_CLASS);
    // Forces a style read between removing and adding the animation class, so a pulse right after another starts again.
    void link.offsetWidth;
    link.classList.add(PULSE_CLASS, ...TINT_CLASSES);

    flight.timers.push(setTimeout(() => link.classList.remove(PULSE_CLASS), LANDED_MS));
    flight.timers.push(setTimeout(() => {
        link.classList.remove(...TINT_CLASSES);

        if (current === flight) {
            current = null;
        }
    }, LANDED_MS + MARK_PULSES_MS));
    // Set after the landing pulse's timer, so that pulse is off before the mark's pulses take the ring.
    markNew(link, LANDED_MS);
}

function fly(flight, from, link, document) {
    const pill = document.createElement("span");

    pill.className = PILL_CLASSES;
    pill.setAttribute("aria-hidden", "true");
    pill.textContent = flight.query;
    document.body.appendChild(pill);
    flight.pill = pill;

    if (typeof pill.animate !== "function") {
        pill.remove();
        pulse(link, flight);

        return;
    }

    const fromBox = from.getBoundingClientRect();
    const pillBox = pill.getBoundingClientRect();
    const animation = pill.animate(flightKeyframes(fromBox, link.getBoundingClientRect(), pillBox), {
        duration: FLIGHT_MS,
        // The keyframes carry the ease (flightKeyframes()); eased again here, the travel would be over at once.
        easing: "linear",
        fill: "forwards",
    });

    flight.animation = animation;
    // While the pill is in the air no list moves in a view transition, whose frozen picture would hold the pill still
    // and then let it jump: the row it left folds once it has landed, as folds always move (holdListMotion()). Let go
    // on the landing, on Undo or a new copy (cancelFlight()), and at the latest a beat past the flight.
    flight.release = holdListMotion();
    flight.timers.push(setTimeout(() => flight.release?.(), FLIGHT_MS + 500));
    // The button opens to its name as the pill leaves, so the search is seen landing in "Target keywords". Opening, it
    // grows (leftwards, in the top bar), so once it is open the landing is aimed at it as it now stands: the pill has
    // barely set off by REAIM_MS, so its change of course is a few pixels.
    flight.opened = true;
    link.classList.add(OPEN_CLASS);
    flight.timers.push(setTimeout(() => {
        if (current === flight && flight.pill !== null) {
            animation.effect?.setKeyframes?.(flightKeyframes(fromBox, link.getBoundingClientRect(), pillBox));
        }
    }, REAIM_MS));
    animation.onfinish = () => {
        if (current !== flight) {
            return;
        }

        pill.remove();
        flight.pill = null;
        pulse(link, flight);
        flight.release();
    };
}

/**
 * Starts a flight for the copy `detail` describes ({activity, rowId, query}), as the drawer starts to leave: from the
 * folded row's name when it has folded, else from the row's name as it stands. The fold waits on two answers from the
 * server (the target's brief read from BigQuery, then the list drawn again), and the flight used to wait for it, up to
 * 1.5 s past the close: on a slow answer the pill never flew and the button pulsed alone (P101, D217). A copy from no
 * row, or from a row the page no longer shows, pulses the button alone.
 */
export function startFlight(detail, document) {
    cancelFlight();

    const link = typeof document.querySelector === "function" ? document.querySelector(LINK_SELECTOR) : null;

    if (link === null || link === undefined) {
        return null;
    }

    // Nothing flies or pulses; the pill still opens to its name and shows its dot, which do not move under reduced motion.
    if (!motionAllowed()) {
        markNew(link);

        return null;
    }

    const flight = { query: String(detail?.query ?? ""), activity: detail?.activity ?? null, rowId: detail?.rowId ?? null, pill: null, animation: null, link, timers: [] };
    const from = foldedName(document, flight) ?? rowName(document, flight);

    current = flight;

    if (from !== null && flight.query !== "") {
        fly(flight, from, link, document);
    } else {
        pulse(link, flight);
    }

    return flight;
}

let isWatching = false;

/** Listens once per page for the drawer's close after a copy and for the Undos that stop a flight. */
export function watch(target = typeof window === "undefined" ? null : window) {
    if (isWatching || target === null || typeof target.addEventListener !== "function") {
        return;
    }

    isWatching = true;
    target.addEventListener(COPY_CLOSED_EVENT, (event) => startFlight(event.detail ?? {}, target.document ?? globalThis.document));
    CANCEL_EVENTS.forEach((name) => target.addEventListener(name, () => {
        cancelFlight();
        unmarkNew();
    }));
}
